import React, { useState, useEffect, useCallback, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, setDoc, onSnapshot, collection, query, deleteDoc, getDocs, runTransaction, getDoc, updateDoc } from 'firebase/firestore';

// --- Firebase and App Configuration ---
const canvasAppId = typeof __app_id !== 'undefined' ? __app_id : null;
const canvasFirebaseConfig = typeof __firebase_config !== 'undefined' ? JSON.parse(__firebase_config) : null;
const canvasInitialAuthToken = typeof __initial_auth_token !== 'undefined' ? __initial_auth_token : null;

const deployedFirebaseConfig = {
    apiKey: "AIzaSyAcJOV5plT76kJS7tClANEoluY0KIftMD8", // Your Firebase API Key
    authDomain: "poker-trakcer-app.firebaseapp.com",
    projectId: "poker-trakcer-app",
    storageBucket: "poker-trakcer-app.firebasestorage.app",
    messagingSenderId: "351657591530",
    appId: "1:351657591530:web:3d988a4583b6c2b9214d93",
    measurementId: "G-QPFDEM9K1N"
};

const activeFirebaseConfig = canvasFirebaseConfig || deployedFirebaseConfig;
const activeAppId = canvasAppId || deployedFirebaseConfig.projectId;
const activeInitialAuthToken = canvasInitialAuthToken;

let app, db, auth;
try {
    app = initializeApp(activeFirebaseConfig);
    db = getFirestore(app);
    auth = getAuth(app);
    console.log("Firebase initialized successfully.");
} catch (initError) {
    console.error("Error during Firebase SDK initialization:", initError);
}

// --- Constants ---
const MIN_PLAYERS = 2;
const DEFAULT_STARTING_BALANCE = 1000.00;
const ROUND_STAGES = ["Pre-Flop", "Flop", "Turn", "River", "Showdown"];

function App() {
    // --- State Management ---
    const [players, setPlayers] = useState([]);
    const [userId, setUserId] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [error, setError] = useState('');
    const [statusMessage, setStatusMessage] = useState('');
    const [playerNameInputs, setPlayerNameInputs] = useState({});

    // Game Session State
    const [gameMode, setGameMode] = useState('lobby');
    const [currentGameId, setCurrentGameId] = useState(null);
    const [joinGameIdInput, setJoinGameIdInput] = useState('');
    const [startingBankBalanceInput, setStartingBankBalanceInput] = useState(DEFAULT_STARTING_BALANCE);

    // Synchronized Game State
    const [hostId, setHostId] = useState(null);
    const [gameStartingBalance, setGameStartingBalance] = useState(DEFAULT_STARTING_BALANCE);
    const [isGameStarted, setIsGameStarted] = useState(false);
    const [currentTurnPlayerId, setCurrentTurnPlayerId] = useState(null);
    const [currentRoundStageIndex, setCurrentRoundStageIndex] = useState(0);
    const [currentBet, setCurrentBet] = useState(0);
    const [bettingPool, setBettingPool] = useState(0);
    const [activePlayerIds, setActivePlayerIds] = useState([]);
    const [lastPlayerToRaiseId, setLastPlayerToRaiseId] = useState(null);

    // Modals State
    const [showConfirmResetModal, setShowConfirmResetModal] = useState(false);
    const [showEndRoundModal, setShowEndRoundModal] = useState(false);
    const [selectedWinnerId, setSelectedWinnerId] = useState('');

    const playerInputBetsRef = useRef({});
    const isHost = userId && hostId === userId;

    // --- Core Effects ---
    useEffect(() => {
        if (!auth) {
            setError("Firebase auth is not available.");
            setIsAuthReady(true);
            return;
        }
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setUserId(user.uid);
            } else {
                try {
                    await (activeInitialAuthToken ? signInWithCustomToken(auth, activeInitialAuthToken) : signInAnonymously(auth));
                } catch (signInError) {
                    setError(`Auth failed: ${signInError.message}`);
                }
            }
            setIsAuthReady(true);
        });
        return () => unsubscribe();
    }, []);
    
    useEffect(() => {
        const savedGameId = sessionStorage.getItem('pokerGameId');
        if (savedGameId) {
            setCurrentGameId(savedGameId);
            setGameMode('inGame');
        }
    }, []);

    useEffect(() => {
        if (!isAuthReady || !userId || !db || !currentGameId) {
            setPlayers([]);
            return;
        }

        sessionStorage.setItem('pokerGameId', currentGameId);

        const gameStateDocRef = doc(db, `artifacts/${activeAppId}/games/${currentGameId}/game_state/current`);
        const unsubscribeGameState = onSnapshot(gameStateDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setHostId(data.hostId || null);
                setGameStartingBalance(data.startingBalance ?? DEFAULT_STARTING_BALANCE);
                setIsGameStarted(data.isGameStarted || false);
                setCurrentTurnPlayerId(data.currentTurnPlayerId || null);
                setCurrentRoundStageIndex(data.currentRoundStageIndex ?? 0);
                setCurrentBet(data.currentBet ?? 0);
                setBettingPool(data.bettingPool ?? 0);
                setActivePlayerIds(data.activePlayerIds || []);
                setLastPlayerToRaiseId(data.lastPlayerToRaiseId || null);
            } else {
                setError("Game session has ended.");
                setCurrentGameId(null);
                setGameMode('lobby');
                sessionStorage.removeItem('pokerGameId');
            }
        }, (err) => setError(`State listener error: ${err.message}`));

        const playersCollectionRef = collection(db, `artifacts/${activeAppId}/games/${currentGameId}/players`);
        const unsubscribePlayers = onSnapshot(query(playersCollectionRef), (snapshot) => {
            const playersData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setPlayers(playersData.sort((a, b) => (a.joinedAt || 0) - (b.joinedAt || 0)));
            setPlayerNameInputs(prev => {
                const newInputs = { ...prev };
                playersData.forEach(p => { if (newInputs[p.id] === undefined) newInputs[p.id] = p.name; });
                return newInputs;
            });
        }, (err) => setError(`Player listener error: ${err.message}`));

        return () => {
            unsubscribeGameState();
            unsubscribePlayers();
        };
    }, [isAuthReady, userId, db, currentGameId, activeAppId]);


    // --- Core Game Logic ---

    const initializeGameState = useCallback(async (gameId, startingBalance, hostUid) => {
        const gameStateDocRef = doc(db, `artifacts/${activeAppId}/games/${gameId}/game_state/current`);
        await setDoc(gameStateDocRef, {
            hostId: hostUid, startingBalance, isGameStarted: false, currentTurnPlayerId: null,
            currentRoundStageIndex: 0, currentBet: 0, bettingPool: 0, activePlayerIds: [],
            lastPlayerToRaiseId: null, roundStartingPlayerId: null,
        });
    }, [db, activeAppId]);

    const nextTurn = useCallback(async (actedPlayerId) => {
        try {
            await runTransaction(db, async (transaction) => {
                const gameStateDocRef = doc(db, `artifacts/${activeAppId}/games/${currentGameId}/game_state/current`);
                const gameStateSnap = await transaction.get(gameStateDocRef);
                if (!gameStateSnap.exists()) return;
                
                let gData = gameStateSnap.data();

                const playersQuery = query(collection(db, `artifacts/${activeAppId}/games/${currentGameId}/players`));
                const playersSnapshot = await getDocs(playersQuery);
                const allPlayers = playersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

                const activePlayersInHand = allPlayers.filter(p => gData.activePlayerIds.includes(p.id));

                const allBetsMatched = activePlayersInHand.every(p => p.contribution === gData.currentBet || p.balance === 0);
                const actionIsBackToRaiser = gData.lastPlayerToRaiseId === actedPlayerId;
                
                if (allBetsMatched && (actionIsBackToRaiser || !gData.lastPlayerToRaiseId)) {
                    // Betting round is over. Advance to the next stage.
                    const newRoundStageIndex = gData.currentRoundStageIndex + 1;
                    if (newRoundStageIndex >= ROUND_STAGES.length -1) {
                        // Showdown
                        transaction.update(gameStateDocRef, { currentRoundStageIndex: ROUND_STAGES.length - 1 });
                        setShowEndRoundModal(true);
                        return;
                    }
                    
                    const firstActivePlayer = allPlayers.find(p => !p.folded);
                    transaction.update(gameStateDocRef, {
                        currentRoundStageIndex: newRoundStageIndex,
                        lastPlayerToRaiseId: null,
                        currentTurnPlayerId: firstActivePlayer?.id || null,
                        roundStartingPlayerId: firstActivePlayer?.id || null,
                    });
                    setStatusMessage(`Advancing to ${ROUND_STAGES[newRoundStageIndex]}!`);
                } else {
                    // Round is not over, just move to the next player.
                    const currentTurnIndex = gData.activePlayerIds.indexOf(gData.currentTurnPlayerId);
                    let nextPlayerId = null;
                    for (let i = 1; i <= gData.activePlayerIds.length; i++) {
                        const nextIndex = (currentTurnIndex + i) % gData.activePlayerIds.length;
                        const candidateId = gData.activePlayerIds[nextIndex];
                        const candidate = allPlayers.find(p => p.id === candidateId);
                        if (candidate && !candidate.folded && candidate.balance > 0) {
                            nextPlayerId = candidate.id;
                            break;
                        }
                    }
                    transaction.update(gameStateDocRef, { currentTurnPlayerId: nextPlayerId });
                }
            });
        } catch(e) {
            setError(`Failed to advance turn: ${e.message}`);
        }
    }, [db, currentGameId, activeAppId, players]);

    const handleEndRound = async () => {
        if (!isHost || !selectedWinnerId) return setError("A winner must be selected.");
        
        const winnerPlayer = players.find(p => p.id === selectedWinnerId);
        if (!winnerPlayer) return setError("Selected winner not found.");

        try {
            await runTransaction(db, async (transaction) => {
                const winnerRef = doc(db, `artifacts/${activeAppId}/games/${currentGameId}/players`, selectedWinnerId);
                const winnerSnap = await transaction.get(winnerRef);
                const newWinnerBalance = (winnerSnap.data().balance || 0) + bettingPool;
                transaction.update(winnerRef, { balance: newWinnerBalance });
                
                const allPlayerIds = players.map(p => p.id);
                players.forEach(p => {
                    const playerRef = doc(db, `artifacts/${activeAppId}/games/${currentGameId}/players`, p.id);
                    transaction.update(playerRef, { contribution: 0, folded: false });
                });

                const firstPlayerId = allPlayerIds[0] || null;
                const gameStateDocRef = doc(db, `artifacts/${activeAppId}/games/${currentGameId}/game_state/current`);
                transaction.update(gameStateDocRef, {
                    currentTurnPlayerId: firstPlayerId, currentRoundStageIndex: 0, currentBet: 0,
                    bettingPool: 0, activePlayerIds: allPlayerIds, lastPlayerToRaiseId: null,
                    roundStartingPlayerId: firstPlayerId,
                });
            });
            setStatusMessage(`${winnerPlayer.name} won $${bettingPool.toFixed(2)}! New hand started.`);
            setShowEndRoundModal(false);
            setSelectedWinnerId('');
        } catch (e) { setError(`Failed to end round: ${e.message}`); }
    };
    
    // --- User Actions ---
    const addValueToPlayerBet = useCallback((playerId, amount) => {
        const currentVal = playerInputBetsRef.current[playerId] || 0;
        const newVal = Math.max(0, currentVal + amount);
        playerInputBetsRef.current[playerId] = newVal;
        setPlayers(prev => prev.map(p => p.id === playerId ? { ...p, uiBetAmount: newVal } : p));
    }, []);
    
    const placeBet = async (playerId) => {
        if (playerId !== currentTurnPlayerId) return setError("It's not your turn.");
        const player = players.find(p => p.id === playerId);
        const betAmount = playerInputBetsRef.current[playerId] || 0;
        if (!player || betAmount <= 0) return setError("Invalid bet amount.");

        try {
            await runTransaction(db, async (transaction) => {
                const playerRef = doc(db, `artifacts/${activeAppId}/games/${currentGameId}/players`, playerId);
                const gameStateRef = doc(db, `artifacts/${activeAppId}/games/${currentGameId}/game_state/current`);
                const [playerSnap, gameStateSnap] = await Promise.all([transaction.get(playerRef), transaction.get(gameStateRef)]);
                
                const pData = playerSnap.data();
                const gData = gameStateSnap.data();
                
                const newTotalContribution = pData.contribution + betAmount;
                if (newTotalContribution <= gData.currentBet) {
                    throw new Error(`Raise must be higher than current bet of $${gData.currentBet.toFixed(2)}.`);
                }
                if (betAmount > pData.balance) {
                    throw new Error("Insufficient balance for this bet.");
                }

                transaction.update(playerRef, { balance: pData.balance - betAmount, contribution: newTotalContribution });
                transaction.update(gameStateRef, { 
                    bettingPool: gData.bettingPool + betAmount,
                    currentBet: newTotalContribution,
                    lastPlayerToRaiseId: playerId
                });
            });
            playerInputBetsRef.current[playerId] = 0;
            await nextTurn(playerId);
        } catch (e) { setError(`Bet failed: ${e.message}`); }
    };

    const callCheck = async (playerId) => {
        if (playerId !== currentTurnPlayerId) return setError("It's not your turn.");
        const player = players.find(p => p.id === playerId);
        if (!player) return;
        
        try {
            await runTransaction(db, async (transaction) => {
                const playerRef = doc(db, `artifacts/${activeAppId}/games/${currentGameId}/players`, playerId);
                const gameStateRef = doc(db, `artifacts/${activeAppId}/games/${currentGameId}/game_state/current`);
                const [playerSnap, gameStateSnap] = await Promise.all([transaction.get(playerRef), transaction.get(gameStateRef)]);

                const pData = playerSnap.data();
                const gData = gameStateSnap.data();
                const amountToCall = gData.currentBet - pData.contribution;
                
                if (amountToCall > pData.balance) throw new Error('Insufficient balance to call.');
                
                if (amountToCall > 0) {
                    transaction.update(playerRef, { balance: pData.balance - amountToCall, contribution: pData.contribution + amountToCall });
                    transaction.update(gameStateRef, { bettingPool: gData.bettingPool + amountToCall });
                }
            });
            await nextTurn(playerId);
        } catch (e) { setError(`Call/Check failed: ${e.message}`); }
    };

    const fold = async (playerId) => {
        if (playerId !== currentTurnPlayerId) return;
        try {
             await runTransaction(db, async (transaction) => {
                const playerRef = doc(db, `artifacts/${activeAppId}/games/${currentGameId}/players`, playerId);
                transaction.update(playerRef, { folded: true });
                const gameStateRef = doc(db, `artifacts/${activeAppId}/games/${currentGameId}/game_state/current`);
                const gData = (await transaction.get(gameStateRef)).data();
                transaction.update(gameStateRef, { activePlayerIds: gData.activePlayerIds.filter(id => id !== playerId) });
             });
             await nextTurn(playerId);
        } catch(e) { setError(`Fold failed: ${e.message}`); }
    };

    const handleCreateGame = async () => {
        if (!userId || !db) return;
        const newGameId = crypto.randomUUID().substring(0, 6);
        try {
            await initializeGameState(newGameId, startingBankBalanceInput, userId);
            const playerRef = doc(db, `artifacts/${activeAppId}/games/${newGameId}/players`, userId);
            await setDoc(playerRef, { name: `Host`, balance: startingBankBalanceInput, contribution: 0, folded: false, joinedAt: Date.now() });
            setCurrentGameId(newGameId);
            setGameMode('inGame');
        } catch (e) { setError(`Failed to create game: ${e.message}`); }
    };

    const handleJoinGame = async () => {
        if (!userId || !db || !joinGameIdInput.trim()) return;
        const trimmedGameId = joinGameIdInput.trim();
        const gameStateDocRef = doc(db, `artifacts/${activeAppId}/games/${trimmedGameId}/game_state/current`);
        try {
            const gameStateSnap = await getDoc(gameStateDocRef);
            if (!gameStateSnap.exists()) return setError("Game ID not found.");
            
            const playerRef = doc(db, `artifacts/${activeAppId}/games/${trimmedGameId}/players`, userId);
            if (!(await getDoc(playerRef)).exists()) {
                await setDoc(playerRef, { name: `Player (${userId.substring(0, 4)})`, balance: gameStateSnap.data().startingBalance, contribution: 0, folded: false, joinedAt: Date.now() });
            }
            setCurrentGameId(trimmedGameId);
            setGameMode('inGame');
        } catch (e) { setError(`Failed to join game: ${e.message}`); }
    };
    
    const handleStartGame = async () => {
        if (!isHost || players.length < MIN_PLAYERS) return;
        try {
            const gameStateDocRef = doc(db, `artifacts/${activeAppId}/games/${currentGameId}/game_state/current`);
            const allPlayerIds = players.map(p => p.id);
            const firstPlayerId = players[0]?.id || null;
            await updateDoc(gameStateDocRef, { isGameStarted: true, activePlayerIds: allPlayerIds, currentTurnPlayerId: firstPlayerId, roundStartingPlayerId: firstPlayerId });
        } catch(e) { setError(`Failed to start game: ${e.message}`); }
    };

    const handleUpdatePlayerName = async (playerId, newName) => {
        if (playerId !== userId || !newName.trim()) return;
        try {
            const playerDocRef = doc(db, `artifacts/${activeAppId}/games/${currentGameId}/players`, playerId);
            await updateDoc(playerDocRef, { name: newName.trim() });
        } catch (e) { setError(`Failed to update name: ${e.message}`); }
    };

    const handleResetGame = async () => {
        if (!isHost) return setError("Only the host can reset the game.");
        try {
            const playersCollectionRef = collection(db, `artifacts/${activeAppId}/games/${currentGameId}/players`);
            const snapshot = await getDocs(query(playersCollectionRef));
            const deletePromises = snapshot.docs.map(d => deleteDoc(d.ref));
            await Promise.all(deletePromises);
            await initializeGameState(currentGameId, gameStartingBalance, userId);
            const hostPlayerRef = doc(db, `artifacts/${activeAppId}/games/${currentGameId}/players`, userId);
            await setDoc(hostPlayerRef, { name: "Host", balance: gameStartingBalance, contribution: 0, folded: false, joinedAt: Date.now() });
            setShowConfirmResetModal(false);
        } catch (e) { setError(`Failed to reset game: ${e.message}`); }
    };

    const handleReturnToLobby = () => {
        sessionStorage.removeItem('pokerGameId');
        setCurrentGameId(null);
        setGameMode('lobby');
    };


    // --- Render Logic ---
    if (!isAuthReady) {
        return <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white"><p className="text-xl">Connecting...</p></div>;
    }

    const nonFoldedPlayers = players.filter(p => activePlayerIds.includes(p.id));

    return (
        <div className="min-h-screen bg-gray-900 text-white font-sans flex flex-col items-center py-8 px-4">
            <h1 className="text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-600 mb-6">Poker Tracker</h1>

            {error && <div className="bg-red-600 text-white p-3 rounded-lg mb-4 w-full max-w-md text-center">{error}</div>}
            {statusMessage && <div className="bg-blue-600 text-white p-3 rounded-lg mb-4 w-full max-w-md text-center">{statusMessage}</div>}

            {gameMode === 'lobby' && (
                <div className="w-full max-w-md bg-gray-800 p-8 rounded-xl shadow-2xl space-y-6">
                     <h2 className="text-3xl font-bold text-center">Lobby</h2>
                     <div className="space-y-4">
                        <h3 className="text-xl font-semibold">Create Game</h3>
                        <input type="number" value={startingBankBalanceInput} onChange={(e) => setStartingBankBalanceInput(parseFloat(e.target.value))} className="w-full p-3 rounded-lg bg-gray-700"/>
                        <button onClick={handleCreateGame} className="w-full mt-3 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg">Create</button>
                    </div>
                    <div className="border-t border-gray-700 pt-6 space-y-4">
                        <h3 className="text-xl font-semibold">Join Game</h3>
                        <input type="text" value={joinGameIdInput} onChange={(e) => setJoinGameIdInput(e.target.value)} placeholder="Game ID" className="w-full p-3 rounded-lg bg-gray-700"/>
                        <button onClick={handleJoinGame} className="w-full mt-3 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg">Join</button>
                    </div>
                </div>
            )}

            {gameMode === 'inGame' && (
                <div className="w-full flex flex-col items-center">
                    <div className="mb-6 text-center bg-gray-800 p-3 rounded-lg w-full max-w-2xl">
                        <p>Game ID: <span className="font-mono text-lg text-yellow-300">{currentGameId}</span> {isHost && <span className="text-purple-400">(Host)</span>}</p>
                    </div>
                    
                    <div className="w-full max-w-4xl bg-gray-800 p-4 rounded-xl mb-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                        <div><span className="block text-sm text-gray-400">Turn</span><span className="text-lg font-semibold text-pink-400 truncate">{players.find(p => p.id === currentTurnPlayerId)?.name || 'N/A'}</span></div>
                        <div><span className="block text-sm text-gray-400">Stage</span><span className="text-lg font-semibold text-green-400">{ROUND_STAGES[currentRoundStageIndex]}</span></div>
                        <div><span className="block text-sm text-gray-400">Bet</span><span className="text-lg font-semibold text-yellow-400">${currentBet.toFixed(2)}</span></div>
                        <div><span className="block text-sm text-gray-400">Pot</span><span className="text-lg font-semibold text-yellow-400">${bettingPool.toFixed(2)}</span></div>
                    </div>

                    {!isGameStarted && (
                        <div className="w-full max-w-2xl bg-gray-800 p-6 rounded-xl mb-6 text-center space-y-4">
                            <h3 className="text-2xl font-bold">Waiting for Players...</h3>
                            {isHost && players.length >= MIN_PLAYERS && <button onClick={handleStartGame} className="w-full py-4 bg-green-500 text-white font-bold text-xl rounded-lg">Start Game</button>}
                        </div>
                    )}

                    <div className="w-full max-w-7xl overflow-x-auto pb-4"><div className="flex space-x-4 p-2 min-w-max">
                        {players.map((player) => (
                            <div key={player.id} className={`flex-none w-80 bg-gray-700 p-5 rounded-xl border-2 transition-all ${currentTurnPlayerId === player.id && !player.folded ? 'border-yellow-400' : 'border-gray-600'} ${player.folded ? 'opacity-40' : ''}`}>
                                <div className="text-center mb-3">
                                    {player.id === userId ? (
                                        <div className="flex gap-2 items-center">
                                            <input value={playerNameInputs[player.id] || ''} onChange={(e) => setPlayerNameInputs({...playerNameInputs, [player.id]: e.target.value})} className="w-full p-2 text-xl font-bold rounded-md bg-gray-600 text-center" />
                                            <button onClick={() => handleUpdatePlayerName(player.id, playerNameInputs[player.id])} className="p-2 bg-green-600 rounded-md text-xs">Save</button>
                                        </div>
                                    ) : ( <h2 className="text-2xl font-bold truncate">{player.name}</h2> )}
                                    {player.folded && <span className="text-red-400 text-sm font-semibold">(FOLDED)</span>}
                                </div>
                                <div className="space-y-1 mb-4 text-lg">
                                    <div className="flex justify-between"><span>Balance:</span><span className="font-bold text-pink-400">${player.balance.toFixed(2)}</span></div>
                                    <div className="flex justify-between"><span>In Pot:</span><span className="font-bold text-cyan-400">${player.contribution.toFixed(2)}</span></div>
                                </div>
                                {isGameStarted && player.id === userId && !player.folded && currentTurnPlayerId === userId && (
                                    <div className="border-t-2 border-gray-600 pt-4 mt-4 space-y-3">
                                        <label className="block text-gray-300 mb-2 text-center">Your Action</label>
                                        <input type="number" min="0" defaultValue={playerInputBetsRef.current[player.id] || 0} onChange={(e) => { playerInputBetsRef.current[player.id] = parseInt(e.target.value) || 0; }} className="w-full p-2 text-center text-2xl font-bold rounded-lg bg-gray-600 text-yellow-300" />
                                        <div className="grid grid-cols-4 gap-2">
                                            {[1, 5, 10, 25].map(amount => <button key={amount} onClick={() => addValueToPlayerBet(player.id, amount)} className="py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-md">+{amount}</button>)}
                                        </div>
                                        <button onClick={() => placeBet(player.id)} className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-md">Place Bet</button>
                                        <div className="grid grid-cols-2 gap-2">
                                            <button onClick={() => callCheck(player.id)} className="py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-md">Call/Check</button>
                                            <button onClick={() => fold(player.id)} className="py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-md">Fold</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div></div>

                    <div className="w-full max-w-md mt-6 space-y-3">
                       {isHost && <button onClick={() => { const firstWinner = nonFoldedPlayers[0]; if (firstWinner) setSelectedWinnerId(firstWinner.id); setShowEndRoundModal(true);}} className="w-full px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-bold rounded-lg">End Round</button>}
                       {isHost && <button onClick={() => setShowConfirmResetModal(true)} className="w-full px-6 py-3 bg-red-700 hover:bg-red-800 text-white font-bold rounded-lg">Reset Game</button>}
                        <button onClick={handleReturnToLobby} className="w-full px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-bold rounded-lg">Return to Lobby</button>
                    </div>

                    {/* Modals */}
                    {showEndRoundModal && isHost && (
                        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
                            <div className="bg-gray-800 p-8 rounded-xl shadow-2xl w-full max-w-md text-center">
                                <h3 className="text-2xl font-bold text-yellow-400 mb-4">End Round & Award Pot</h3>
                                <p className="text-gray-300 mb-4">Select the winner of the <span className="font-bold">${bettingPool.toFixed(2)}</span> pot:</p>
                                <select value={selectedWinnerId} onChange={(e) => setSelectedWinnerId(e.target.value)} className="w-full p-3 rounded-lg bg-gray-700 text-white mb-6">
                                    <option value="" disabled>Select a player...</option>
                                    {nonFoldedPlayers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                                <div className="flex justify-center space-x-4">
                                    <button onClick={handleEndRound} className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg" disabled={!selectedWinnerId}>Award Pot & Next Hand</button>
                                    <button onClick={() => setShowEndRoundModal(false)} className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg">Cancel</button>
                                </div>
                            </div>
                        </div>
                    )}
                    {showConfirmResetModal && isHost && (
                        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
                            <div className="bg-gray-800 p-8 rounded-xl w-full max-w-md text-center">
                                 <h3 className="text-2xl font-bold text-red-400 mb-4">Confirm Reset</h3>
                                 <p className="mb-6">This will delete all players and reset all progress. Are you sure?</p>
                                <div className="flex justify-center space-x-4">
                                    <button onClick={handleResetGame} className="px-6 py-3 bg-red-700 font-bold rounded-lg">Yes, Reset</button>
                                    <button onClick={() => setShowConfirmResetModal(false)} className="px-6 py-3 bg-gray-600 rounded-lg">Cancel</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default App;
